import { HOME_BOARD } from "@shared/plan-home/home-board.generated";
import { CELL_FIELD } from "@shared/interview/board-map";
import { PERSONAL_TEMPLATE } from "@shared/interview/board-template";
import { FINAL_TOUCHES, INTERVIEW_CHAPTERS, RETIRED_SCREEN_KEYS, type InterviewPosition, type InterviewScreen } from "@shared/interview/chapters";

/**
 * PLAN-HOME-1 (s228) — the plan's home reads board 12 (`HOME_BOARD`, generated from Home.dc.html). RULE 137: the
 * words below come from the board; this file only fills the board's `{planner}` / `{executor}` / `{n}` / `{label}`.
 */

export { HOME_BOARD };

export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? String(values[k]) : m));
}

/** s237 PR 4 (D-236.88-7, Reade "agree"; the s237 ruling "ruled words in code"): places a visit can come from that board
 *  12 does not name — chapter two's "Change it there" opens Personal details with a way back to chapter two. */
export const RULED_PLACES: Readonly<Record<string, string>> = { "chapter-2": "Chapter two" };

/** s237 (Reade, D-237.64, D-237.65; the s237 ruling "ruled words in code") — the executor's words on Where things are,
 *  ruled but not yet drawn on board 12's executor view (Claude.ai draws them next canvas pass). `{planner}` is the
 *  planner's first name. The planner's own words stay the board's (`HOME_BOARD.where`). */
export const RULED_EXECUTOR_WORDS = {
  whereNote: "Every place {planner} told us something is kept, in one list. Answer or change any of them here.",
  keptHere: "Kept here, in {planner}'s plan",
  /** D-237.75: the executor dashboard's tile that opens Where things are (was the classic Where Is Everything). */
  whereTile: "Every place {planner} told us something is kept, in one list.",
} as const;

/** D-237.65 on the Things on no page line: board 12's "kept here, in your plan — …" with the planner's plan named. Pure;
 *  throws if the board's line no longer carries "in your plan" (a ruled word must not silently stop applying). */
export function executorNoPageSub(planner: string): string {
  const sub = HOME_BOARD.where.noPage.sub;
  if (!sub.includes("in your plan")) throw new Error(`D-237.65: board 12's Things on no page line no longer says "in your plan": "${sub}"`);
  return sub.replace("in your plan", `in ${planner}'s plan`);
}

/** The page a visit came from, as board 12 names it — `from` is a board page id (`home`, `settings`) or a ruled place;
 *  anything else is "my plan", the board's fallback. */
export function returnPlace(from: string): string {
  return (HOME_BOARD.labels as Record<string, string>)[from] ?? RULED_PLACES[from] ?? "my plan";
}

/** Where each place a visit can come from lives. */
const PATH_OF: Readonly<Record<string, string>> = { home: "/dashboard", settings: "/settings", interview: "/interview", where: "/plan/where", contacts: "/plan/contacts" };

/** Where a visit's Return goes. s237 PR 4 (D-236.88-7): back to chapter two's Home and property — the interview, which
 *  stands at that screen, while it is in progress; else the screen itself, opened from the home. Pure. */
export function returnPath(from: string, inProgress: boolean): string {
  if (from === "chapter-2") return inProgress ? "/interview" : "/plan/screen/prop?from=home";
  return PATH_OF[from] ?? "/dashboard";
}

/** "Return to Home" (board 12's `backLabel`). */
export function returnLabel(from: string): string {
  return fill(HOME_BOARD.backLabel, { label: returnPlace(from) });
}

/** A chapter's sidebar title: board 12's, by the chapter's number. */
export function chapterTitle(n: number): string {
  return HOME_BOARD.chapters.find((c) => c.number === n)?.title ?? "";
}

/** Where a screen opens from the plan: its interview position, or a Final-touches step. */
export type PlanTarget = { kind: "screen"; position: InterviewPosition } | { kind: "touch"; key: string };

export function positionOfKey(key: string): InterviewPosition | undefined {
  const k = RETIRED_SCREEN_KEYS[key] ?? key; // s233 (D-233.81): `your-home` opens Home and property
  for (const c of INTERVIEW_CHAPTERS) {
    const step = c.screens.findIndex((s) => s.key === k);
    if (step >= 0) return { chapter: c.number, step };
  }
  return undefined;
}

export function finalTouch(key: string): InterviewScreen | undefined {
  return FINAL_TOUCHES.find((s) => s.key === key);
}

/**
 * s233 (D-233.74; board 12 `openable` + `extras`) — the page a hand-over's "Open the page ›" opens (`page-<id>`): print
 * your plan, the recovery code page, the family meeting. None is built yet, so each is a placeholder like Final touches,
 * titled by its row. `{executor}` is left for the caller.
 */
export function handoverPage(key: string): InterviewScreen | undefined {
  const row = HOME_BOARD.ownList.rows.find((r) => r.page && `page-${r.page}` === key);
  if (!row) return undefined;
  return { key, crumb: row.label, title: row.label, why: "[SPEC copy pending] — this page is built later.", kind: "placeholder" };
}

/**
 * The fields each chapter-one screen asks — board 1's cells (`CELL_FIELD`) and the two template screens. Used to
 * send a gap to the ONE screen that asks it when two screens share a section (kids/daily on the children rows).
 */
function fieldsOf(key: string): ReadonlySet<string> {
  if (key === "you") return new Set(Object.keys(PERSONAL_TEMPLATE));
  const cells = CELL_FIELD[key];
  return new Set(Object.values(cells ?? {}).flatMap((v) => v ?? []));
}

/**
 * "Where you go" for a gap (board 12's needs panel groups by it): the interview screen that asks this section's
 * field. `undefined` when no built screen asks it — the gap still shows, grouped by its section, answerable in
 * place, with nowhere to open.
 */
export function screenForGap(sectionSlug: string, fieldKey: string): { key: string; chapter: number; step: number; crumb: string } | undefined {
  const hits: { key: string; chapter: number; step: number; crumb: string }[] = [];
  for (const c of INTERVIEW_CHAPTERS) {
    c.screens.forEach((s, step) => {
      if (s.kind === "question" && s.section === sectionSlug) hits.push({ key: s.key, chapter: c.number, step, crumb: s.crumb });
    });
  }
  if (hits.length <= 1) return hits[0];
  return hits.find((h) => fieldsOf(h.key).has(fieldKey)) ?? hits[0];
}
